import type {
  ClauseLibraryControllerListV1Params,
  ContractsControllerListV1Params,
  ContractsControllerRenewalsV1Params,
  ContractsControllerVersionsV1Params,
} from '@/api/generated/model';

/**
 * Query keys for the contracts inventory.
 *
 * In `shared` rather than inside `modules/contracts` for the same reason
 * `auditLogKeys` is: players, onboarding and offboarding all read a player's
 * contracts, and `contracts` imports the `players` barrel for its pickers — a
 * key factory in the module would close that loop. A key is data, not UI, so
 * it has no module to belong to.
 *
 * `lists()` covers the register, the renewals view and the summary tiles: a
 * write to any contract can move all three, and invalidating them together is
 * cheaper than reasoning about which one it moved.
 */
export const contractKeys = {
  all: ['contracts'] as const,
  lists: () => [...contractKeys.all, 'list'] as const,
  list: (params: ContractsControllerListV1Params) =>
    [...contractKeys.lists(), 'register', params] as const,
  renewals: (params: ContractsControllerRenewalsV1Params) =>
    [...contractKeys.lists(), 'renewals', params] as const,
  summary: () => [...contractKeys.lists(), 'summary'] as const,
  details: () => [...contractKeys.all, 'detail'] as const,
  detail: (id: string) => [...contractKeys.details(), id] as const,
  versions: (id: string, params: ContractsControllerVersionsV1Params) =>
    [...contractKeys.detail(id), 'versions', params] as const,
  library: () => [...contractKeys.all, 'clause-library'] as const,
  libraryList: (params: ClauseLibraryControllerListV1Params) =>
    [...contractKeys.library(), params] as const,
  systemClauses: () => [...contractKeys.library(), 'system'] as const,
};
